
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

type Produto = {
  id: string;
  nome: string;
  descricao: string;
  tipo: 'curso' | 'ebook';
  preco: number;
  imagem_url: string;
  categoria: string;
  duracao: string;
  nivel: string;
  recomendado: boolean;
};

type FiltroOrdem =
  | 'recomendado'
  | 'preco_asc'
  | 'preco_desc'
  | 'alfabetico'
  | 'nivel_iniciante'
  | 'nivel_intermediario'
  | 'nivel_avancado';

export default function ProdutosPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [loading, setLoading] = useState(true);
  const [comprando, setComprando] = useState<string | null>(null);
  const [conteudo, setConteudo] = useState<any>(null);
  const [showFiltros, setShowFiltros] = useState(false);
  const [filtroAtivo, setFiltroAtivo] = useState<FiltroOrdem>('recomendado');

  useEffect(() => {
    carregarProdutos();
    carregarConteudo();
  }, []);

  /** Carrega o conteúdo da seção hero */
  const carregarConteudo = async () => {
    try {
      const { data, error } = await supabase
        .from('conteudo_produtos')
        .select('*')
        .eq('secao', 'hero')
        .single();

      if (error) throw error;
      if (data) setConteudo(data);
    } catch (err) {
      console.error('Erro ao carregar conteúdo hero:', err);
    }
  };

  /** Carrega a lista de produtos ativos */
  const carregarProdutos = async () => {
    try {
      const { data, error } = await supabase
        .from('produtos')
        .select('*')
        .eq('ativo', true);

      if (error) throw error;
      setProdutos(data ?? []);
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
    } finally {
      setLoading(false);
    }
  };

  /** Inicia o fluxo de checkout para o produto selecionado */
  const handleComprar = async (produto: Produto) => {
    if (!user) {
      alert('Por favor, faça login para comprar');
      navigate('/login');
      return;
    }

    setComprando(produto.id);

    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          produtoId: produto.id,
          userId: user.id,
          preco: produto.preco,
          nome: produto.nome,
          imagem: produto.imagem_url,
          tipo: produto.tipo, // ✅ Agora incluímos o tipo do produto
        },
      });

      if (error) throw error;

      if (data?.url) {
        // Redireciona para a página de checkout do Stripe
        window.location.href = data.url;
      } else {
        throw new Error('URL de checkout não retornada');
      }
    } catch (err: any) {
      console.error('Erro ao criar checkout:', err);
      alert('Erro ao processar pagamento: ' + (err?.message ?? 'Erro desconhecido'));
    } finally {
      setComprando(null);
    }
  };

  /** Atualiza o filtro ativo e fecha o dropdown */
  const aplicarFiltro = (filtro: FiltroOrdem) => {
    setFiltroAtivo(filtro);
    setShowFiltros(false);
  };

  /** Produtos ordenados de acordo com o filtro ativo */
  const produtosOrdenados = [...produtos].sort((a, b) => {
    // Recomendados sempre primeiro
    if (a.recomendado && !b.recomendado) return -1;
    if (!a.recomendado && b.recomendado) return 1;

    switch (filtroAtivo) {
      case 'preco_asc':
        return a.preco - b.preco;
      case 'preco_desc':
        return b.preco - a.preco;
      case 'alfabetico':
        return a.nome.localeCompare(b.nome);
      case 'nivel_iniciante':
        return a.nivel === 'iniciante' ? -1 : b.nivel === 'iniciante' ? 1 : 0;
      case 'nivel_intermediario':
        return a.nivel === 'intermediario' ? -1 : b.nivel === 'intermediario' ? 1 : 0;
      case 'nivel_avancado':
        return a.nivel === 'avancado' ? -1 : b.nivel === 'avancado' ? 1 : 0;
      default:
        return 0;
    }
  });

  /** Texto amigável para o filtro selecionado */
  const getNomeFiltro = (filtro: FiltroOrdem) => {
    switch (filtro) {
      case 'recomendado':
        return 'Recomendados';
      case 'preco_asc':
        return 'Menor Preço';
      case 'preco_desc':
        return 'Maior Preço';
      case 'alfabetico':
        return 'A-Z';
      case 'nivel_iniciante':
        return 'Iniciante';
      case 'nivel_intermediario':
        return 'Intermédio';
      case 'nivel_avancado':
        return 'Avançado';
      default:
        return '';
    }
  };

  // Conteúdo estático (fallbacks)
  const titulo = conteudo?.titulo ?? 'Os Nossos Produtos';
  const subtitulo = conteudo?.subtitulo ?? 'Cursos e E-books Premium para o Seu Sucesso';
  const descricao =
    conteudo?.descricao ??
    'Explore a nossa seleção de cursos online e e-books criados por especialistas. Conteúdo de qualidade para impulsionar a sua carreira e alcançar os seus objetivos profissionais.';

  return (
    <div className="min-h-screen bg-black">
      <Header />

      <main className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {titulo.split(' ').slice(0, -1).join(' ')}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
                {titulo.split(' ').slice(-1)}
              </span>
            </h1>
            <p className="text-xl text-gray-400 mb-4">{subtitulo}</p>
            <p className="text-gray-500 max-w-3xl mx-auto">{descricao}</p>
          </motion.div>

          {/* Botão de Filtros */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex justify-end mb-8 relative"
          >
            <button
              onClick={() => setShowFiltros(!showFiltros)}
              className="px-6 py-3 bg-gray-900 text-white border border-gray-800 rounded-lg hover:border-yellow-400/50 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-filter-3-line"></i>
              Filtrar: {getNomeFiltro(filtroAtivo)}
              <i
                className={`ri-arrow-down-s-line transition-transform ${showFiltros ? 'rotate-180' : ''}`}
              ></i>
            </button>

            {/* Dropdown de Filtros */}
            <AnimatePresence>
              {showFiltros && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full right-0 mt-2 w-64 bg-gray-900 border border-gray-800 rounded-xl overflow-hidden z-50 shadow-2xl"
                >
                  <div className="p-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wider px-3 py-2">
                      Ordenar por
                    </p>

                    <button
                      onClick={() => aplicarFiltro('recomendado')}
                      className={`w-full px-4 py-3 text-left rounded-lg transition-all cursor-pointer flex items-center gap-3 ${
                        filtroAtivo === 'recomendado'
                          ? 'bg-yellow-400/20 text-yellow-400'
                          : 'text-gray-300 hover:bg-gray-800'
                      }`}
                    >
                      <i className="ri-star-line"></i>
                      Recomendados
                    </button>

                    <button
                      onClick={() => aplicarFiltro('preco_asc')}
                      className={`w-full px-4 py-3 text-left rounded-lg transition-all cursor-pointer flex items-center gap-3 ${
                        filtroAtivo === 'preco_asc'
                          ? 'bg-yellow-400/20 text-yellow-400'
                          : 'text-gray-300 hover:bg-gray-800'
                      }`}
                    >
                      <i className="ri-sort-asc"></i>
                      Menor Preço
                    </button>

                    <button
                      onClick={() => aplicarFiltro('preco_desc')}
                      className={`w-full px-4 py-3 text-left rounded-lg transition-all cursor-pointer flex items-center gap-3 ${
                        filtroAtivo === 'preco_desc'
                          ? 'bg-yellow-400/20 text-yellow-400'
                          : 'text-gray-300 hover:bg-gray-800'
                      }`}
                    >
                      <i className="ri-sort-desc"></i>
                      Maior Preço
                    </button>

                    <button
                      onClick={() => aplicarFiltro('alfabetico')}
                      className={`w-full px-4 py-3 text-left rounded-lg transition-all cursor-pointer flex items-center gap-3 ${
                        filtroAtivo === 'alfabetico'
                          ? 'bg-yellow-400/20 text-yellow-400'
                          : 'text-gray-300 hover:bg-gray-800'
                      }`}
                    >
                      <i className="ri-sort-alphabet-asc"></i>
                      Ordem Alfabética
                    </button>

                    <div className="border-t border-gray-800 my-2"></div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider px-3 py-2">
                      Por Nível
                    </p>

                    <button
                      onClick={() => aplicarFiltro('nivel_iniciante')}
                      className={`w-full px-4 py-3 text-left rounded-lg transition-all cursor-pointer flex items-center gap-3 ${
                        filtroAtivo === 'nivel_iniciante'
                          ? 'bg-yellow-400/20 text-yellow-400'
                          : 'text-gray-300 hover:bg-gray-800'
                      }`}
                    >
                      <i className="ri-seedling-line"></i>
                      Iniciante
                    </button>

                    <button
                      onClick={() => aplicarFiltro('nivel_intermediario')}
                      className={`w-full px-4 py-3 text-left rounded-lg transition-all cursor-pointer flex items-center gap-3 ${
                        filtroAtivo === 'nivel_intermediario'
                          ? 'bg-yellow-400/20 text-yellow-400'
                          : 'text-gray-300 hover:bg-gray-800'
                      }`}
                    >
                      <i className="ri-bar-chart-line"></i>
                      Intermédio
                    </button>

                    <button
                      onClick={() => aplicarFiltro('nivel_avancado')}
                      className={`w-full px-4 py-3 text-left rounded-lg transition-all cursor-pointer flex items-center gap-3 ${
                        filtroAtivo === 'nivel_avancado'
                          ? 'bg-yellow-400/20 text-yellow-400'
                          : 'text-gray-300 hover:bg-gray-800'
                      }`}
                    >
                      <i className="ri-rocket-line"></i>
                      Avançado
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Overlay para fechar dropdown */}
          {showFiltros && (
            <div className="fixed inset-0 z-40" onClick={() => setShowFiltros(false)}></div>
          )}

          {/* Grid de Produtos */}
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
              <p className="text-gray-400 mt-4">A carregar produtos...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
              {produtosOrdenados.map((produto, index) => (
                <motion.div
                  key={produto.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-gradient-to-br from-gray-900 to-black border rounded-2xl overflow-hidden hover:border-yellow-400/50 transition-all group ${
                    produto.recomendado ? 'border-yellow-400/50 ring-2 ring-yellow-400/20' : 'border-gray-800'
                  }`}
                >
                  {/* Imagem */}
                  <div className="relative w-full h-64 overflow-hidden">
                    <img
                      src={produto.imagem_url}
                      alt={produto.nome}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 flex gap-2">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold ${
                          produto.tipo === 'curso' ? 'bg-yellow-400 text-black' : 'bg-gray-400 text-black'
                        }`}
                      >
                        {produto.tipo === 'curso' ? 'CURSO' : 'E-BOOK'}
                      </span>
                    </div>
                    {produto.recomendado && (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-yellow-400 to-orange-500 text-black flex items-center gap-1">
                          <i className="ri-star-fill"></i>
                          RECOMENDADO
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Conteúdo */}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">
                        {produto.categoria}
                      </span>
                      <span className="text-gray-700">•</span>
                      <span className="text-xs text-gray-500 capitalize">
                        {produto.nivel === 'intermediario' ? 'Intermédio' : produto.nivel}
                      </span>
                    </div>

                    <h3
                      className="text-xl font-bold text-white mb-3 group-hover:text-yellow-400 transition-colors cursor-pointer"
                      onClick={() => navigate(`/produto/${produto.id}`)}
                    >
                      {produto.nome}
                    </h3>

                    <p className="text-gray-400 text-sm mb-4 line-clamp-3">{produto.descricao}</p>

                    <div className="flex items-center gap-2 mb-6 text-sm text-gray-500">
                      <i className="ri-time-line"></i>
                      <span>{produto.duracao}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-3xl font-bold text-white">
                          R${produto.preco.toFixed(2)}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 mt-6">
                      <button
                        onClick={() => navigate(`/produto/${produto.id}`)}
                        className="w-full px-4 py-3 bg-gray-900 text-gray-300 border border-gray-800 font-bold rounded-lg hover:bg-gray-800 hover:text-white transition-all cursor-pointer whitespace-nowrap"
                      >
                        Ver Detalhes
                      </button>
                      <button
                        onClick={() => handleComprar(produto)}
                        disabled={comprando === produto.id}
                        className="w-full px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
                      >
                        {comprando === produto.id ? (
                          <>
                            <i className="ri-loader-4-line animate-spin mr-2"></i>
                            A processar...
                          </>
                        ) : (
                          <>
                            <i className="ri-shopping-cart-line mr-2"></i>
                            Comprar
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Mensagem quando não houver produtos */}
          {produtosOrdenados.length === 0 && !loading && (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-6xl text-gray-700 mb-4"></i>
              <p className="text-gray-400 text-lg">Nenhum produto encontrado</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
